import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EmptyState from '../../common/EmptyState';
import ChangePositionModal from './ChangePositionModal';
import './StyleTab.css';

const EmployeesTab = ({ 
  positions = [], 
  brigades = [], 
  departments = [], 
  employees = [], 
  isLoading = false,
  onDataChange 
}) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [employeeToEdit, setEmployeeToEdit] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [newEmployee, setNewEmployee] = useState({ 
    last_name: '', 
    first_name: '', 
    middle_name: '', 
    position_id: '',
    department_id: '',
    personnel_number: ''
  });
  const [searchEmployee, setSearchEmployee] = useState('');
  const [positionFilter, setPositionFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  
  // Состояния для смены должности
  const [isChangePositionModalOpen, setIsChangePositionModalOpen] = useState(false);
  const [selectedPositionChange, setSelectedPositionChange] = useState({ 
    newPositionId: null, 
    employee: null,
    currentTrainings: []
  });
  const [selectedNewPositionId, setSelectedNewPositionId] = useState('');

  // Обработчик клика по сотруднику для перехода на страницу обучений
  const handleEmployeeClick = (employee) => {
    navigate(`/settings/employees/${employee.id}/trainings`);
  };

  // Показ уведомления
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  // Формирование ФИО из частей
  const getFullName = (lastName, firstName, middleName) => {
    const parts = [];
    if (lastName) parts.push(lastName);
    if (firstName) parts.push(firstName);
    if (middleName) parts.push(middleName);
    return parts.join(' ') || '—';
  };

  // Форматирование сотрудников для отображения с проверкой на типы
  const formattedEmployees = employees.map(emp => ({
    id: emp.id,
    last_name: emp.last_name || '',
    first_name: emp.first_name || '',
    middle_name: emp.middle_name || '',
    full_name: emp.full_name || getFullName(emp.last_name, emp.first_name, emp.middle_name),
    personnel_number: emp.personnel_number || '—',
    position_id: emp.position_id || '',
    position: emp.position || emp.position_name || '—',
    department_id: emp.department_id || '',
    department: emp.department || emp.department_name || '—'
  }));

  // Добавление сотрудника
  const addEmployee = async () => {
    if (!newEmployee.last_name || !newEmployee.first_name) {
      showToast('Введите фамилию и имя сотрудника', 'error');
      return;
    }
    
    const requestData = {
      last_name: newEmployee.last_name,
      first_name: newEmployee.first_name,
      middle_name: newEmployee.middle_name || null,
      personnel_number: newEmployee.personnel_number || null
    };
    
    if (newEmployee.position_id && newEmployee.position_id !== '') {
      requestData.position_id = parseInt(newEmployee.position_id);
    }
    
    if (newEmployee.department_id && newEmployee.department_id !== '') {
      requestData.department_id = parseInt(newEmployee.department_id);
    }
    
    setLoading(true);
    try {
      const { employeeService } = await import('../../../services/employeeService');
      await employeeService.create(requestData);
      
      if (onDataChange) {
        await onDataChange();
      }
      
      setNewEmployee({ 
        last_name: '', 
        first_name: '', 
        middle_name: '', 
        position_id: '',
        department_id: '',
        personnel_number: ''
      });
      setIsModalOpen(false);
      showToast('Сотрудник успешно добавлен', 'success');
    } catch (err) {
      console.error('Ошибка добавления сотрудника:', err);
      
      if (err.status === 422 && err.data) {
        let errorMessage = 'Ошибка валидации:\n';
        Object.entries(err.data).forEach(([field, errors]) => {
          if (Array.isArray(errors)) {
            errorMessage += `${field}: ${errors.join(', ')}\n`;
          } else if (typeof errors === 'string') {
            errorMessage += `${field}: ${errors}\n`;
          }
        });
        showToast(errorMessage, 'error');
      } else {
        showToast(`Ошибка добавления: ${err.message}`, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Открытие модального окна редактирования
  const openEditModal = (employee, e) => {
    if (e) e.stopPropagation();
    setEmployeeToEdit({
      id: employee.id,
      last_name: employee.last_name || '',
      first_name: employee.first_name || '',
      middle_name: employee.middle_name || '',
      personnel_number: employee.personnel_number === '—' ? '' : employee.personnel_number,
      position_id: employee.position_id || '',
      position: employee.position,
      department_id: employee.department_id || '',
      department: employee.department
    });
    setSelectedNewPositionId('');
    setIsEditModalOpen(true);
  };

  // Функция для открытия модального окна смены должности
  const handleOpenPositionChange = async (employee, newPositionId) => {
    setLoading(true);
    try {
      const { trainingService } = await import('../../../services/trainingService');
      
      // Получаем текущие обучения сотрудника
      const response = await trainingService.getByEmployee(employee.id);
      let currentTrainings = [];
      
      if (response.trainings) {
        currentTrainings = response.trainings;
      } else if (Array.isArray(response)) {
        currentTrainings = response;
      }
      
      // Нормализуем данные обучений - сохраняем и training.id, и course_id
      const normalizedTrainings = currentTrainings.map(t => ({
        id: t.id,                          // ID записи обучения (для отображения)
        course_id: t.course_id || t.courseId,  // ID курса (для отправки на бэкенд)
        name: t.name || t.course_name,
        status: t.completedDate ? 'completed' : 
                t.daysLeft <= 0 ? 'expired' : 
                t.daysLeft <= 30 ? 'warning' : 'active',
        completedDate: t.completedDate,
        expiresDate: t.expiresDate,
        isRequired: !t.expiresDate,
        isPerpetual: t.periodicity_months === 0 || t.periodicity_months === null,
        certificateNumber: t.certificateNumber || t.certificate_number,
        daysLeft: t.daysLeft
      }));
      
      setSelectedPositionChange({
        newPositionId: parseInt(newPositionId),
        employee: employee,
        currentTrainings: normalizedTrainings
      });
      
      setIsChangePositionModalOpen(true);
      setIsEditModalOpen(false);
    } catch (err) {
      console.error('Ошибка загрузки обучений сотрудника:', err);
      showToast('Не удалось загрузить обучения сотрудника', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Функция подтверждения смены должности
  const confirmPositionChange = async (newPositionId, selectedCourseIds) => {
    setLoading(true);
    try {
      const { employeeService } = await import('../../../services/employeeService');
      
      // selectedCourseIds уже содержит массив course_id (ID курсов)
      console.log('Сохраняемые ID курсов (course_id):', selectedCourseIds);
      
      // Отправляем запрос на смену должности
      await employeeService.changePosition(
        selectedPositionChange.employee.id,
        parseInt(newPositionId),
        selectedCourseIds
      );
      
      // Обновляем данные
      if (onDataChange) {
        await onDataChange();
      }
      
      const newPositionName = positions.find(p => p.id === parseInt(newPositionId))?.name || '';
      showToast(`Должность сотрудника изменена на "${newPositionName}"`, 'success');
      
      setIsChangePositionModalOpen(false);
      setSelectedPositionChange({ newPositionId: null, employee: null, currentTrainings: [] });
      setSelectedNewPositionId('');
    } catch (err) {
      console.error('Ошибка смены должности:', err);
      showToast(`Ошибка: ${err.message || 'Не удалось изменить должность'}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Обновление сотрудника через модальное окно
  const updateEmployee = async () => {
    if (!employeeToEdit.last_name || !employeeToEdit.first_name) {
      showToast('Введите фамилию и имя сотрудника', 'error');
      return;
    }
    
    const requestData = {
      last_name: employeeToEdit.last_name,
      first_name: employeeToEdit.first_name,
      middle_name: employeeToEdit.middle_name || null,
      personnel_number: employeeToEdit.personnel_number || null
    };
    
    // Примечание: должность не отправляем при обычном редактировании,
    // так как для смены должности используется отдельный механизм
    
    if (employeeToEdit.department_id && employeeToEdit.department_id !== '') {
      requestData.department_id = parseInt(employeeToEdit.department_id);
    }
    
    setLoading(true);
    try {
      const { employeeService } = await import('../../../services/employeeService');
      await employeeService.update(employeeToEdit.id, requestData);
      
      if (onDataChange) {
        await onDataChange();
      }
      
      setIsEditModalOpen(false);
      setEmployeeToEdit(null);
      showToast('Сотрудник успешно обновлен', 'success');
    } catch (err) {
      console.error('Ошибка обновления сотрудника:', err);
      
      if (err.status === 422 && err.data) {
        let errorMessage = 'Ошибка валидации:\n';
        Object.entries(err.data).forEach(([field, errors]) => {
          if (Array.isArray(errors)) {
            errorMessage += `${field}: ${errors.join(', ')}\n`;
          }
        });
        showToast(errorMessage, 'error');
      } else {
        showToast(`Ошибка обновления: ${err.message}`, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Открытие модального окна подтверждения удаления
  const openDeleteModal = (employee) => {
    setEmployeeToDelete(employee);
    setIsDeleteModalOpen(true);
  };

  // Закрытие модального окна подтверждения удаления
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setEmployeeToDelete(null);
  };

  // Выполнение удаления
  const confirmDelete = async () => {
    if (!employeeToDelete) return;
    
    setLoading(true);
    try {
      const { employeeService } = await import('../../../services/employeeService');
      await employeeService.delete(employeeToDelete.id);
      
      if (onDataChange) {
        await onDataChange();
      }
      
      showToast(`Сотрудник "${employeeToDelete.full_name}" успешно удален`, 'success');
      closeDeleteModal();
    } catch (err) {
      console.error('Ошибка удаления сотрудника:', err);
      showToast(`Ошибка удаления: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Фильтрация сотрудников
  const filteredEmployees = formattedEmployees.filter(emp => {
    const matchesSearch = searchEmployee === '' || 
      emp.full_name.toLowerCase().includes(searchEmployee.toLowerCase()) ||
      emp.position.toLowerCase().includes(searchEmployee.toLowerCase()) ||
      emp.department.toLowerCase().includes(searchEmployee.toLowerCase()) ||
      emp.personnel_number.toLowerCase().includes(searchEmployee.toLowerCase());
    
    const matchesPosition = positionFilter === 'all' || emp.position === positionFilter;
    
    const matchesDepartment = departmentFilter === 'all' || emp.department === departmentFilter;
    
    return matchesSearch && matchesPosition && matchesDepartment;
  });

  // Состояние загрузки
  if (isLoading && formattedEmployees.length === 0) {
    return (
      <div className="directory-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Загрузка сотрудников...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="directory-container">
      {/* Toast уведомления */}
      {toast.show && (
        <div className={`toast-notification ${toast.type}`}>
          <span className="toast-icon">
            {toast.type === 'success' ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            )}
          </span>
          <span className="toast-message">{toast.message}</span>
        </div>
      )}

      <div className="directory-header">
        <h2>Справочник сотрудников</h2>
        <button className="btn-primary" onClick={() => setIsModalOpen(true)} disabled={loading || isLoading}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Добавить сотрудника
        </button>
      </div>

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Поиск по имени, должности, подразделению или табельному номеру..."
          className="search-input"
          value={searchEmployee}
          onChange={(e) => setSearchEmployee(e.target.value)}
        />
        <select className="filter-select" value={positionFilter} onChange={(e) => setPositionFilter(e.target.value)}>
          <option value="all">Все должности</option>
          {positions.map(pos => (
            <option key={pos.id} value={pos.name}>{pos.name}</option>
          ))}
        </select>
        <select className="filter-select" value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
          <option value="all">Все подразделения</option>
          {departments?.map(dept => (
            <option key={dept.id} value={dept.name}>{dept.name}</option>
          ))}
        </select>
        <button 
          className="btn-secondary" 
          onClick={() => onDataChange?.()} 
          disabled={loading || isLoading}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
            <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1 0-18c2.5 0 4.75 1 6.38 2.62" />
            <path d="M21 3v6h-6" />
          </svg>
          Обновить
        </button>
      </div>

      <div className="employees-table-wrapper">
        <table className="directory-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Таб. №</th>
              <th>ФИО</th>
              <th>Должность</th>
              <th>Подразделение</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map(employee => (
              <tr 
                key={employee.id}
                onClick={() => handleEmployeeClick(employee)}
                className="employee-row"
                style={{ cursor: 'pointer' }}
              >
                <td>{employee.id}</td>
                <td>{employee.personnel_number}</td>
                <td>{employee.full_name}</td>
                <td>{employee.position}</td>
                <td>{employee.department}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button 
                      className="icon-btn small" 
                      onClick={(e) => openEditModal(employee, e)} 
                      title="Редактировать" 
                      disabled={loading}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 3l4 4-7 7H10v-4l7-7z" />
                        <path d="M3 21h18" />
                      </svg>
                    </button>
                    <button 
                      className="icon-btn small delete" 
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteModal(employee);
                      }} 
                      title="Удалить"
                      disabled={loading}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0h10" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredEmployees.length === 0 && !isLoading && (
          <EmptyState 
            icon={
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            } 
            title="Сотрудники не найдены" 
            subtitle="Попробуйте изменить поиск или добавьте нового сотрудника" 
          />
        )}
      </div>

      {/* Модальное окно для добавления сотрудника */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Добавление нового сотрудника</h3>
              <button className="modal-close" onClick={() => setIsModalOpen(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Табельный номер</label>
                <input 
                  type="text" 
                  placeholder="Введите табельный номер" 
                  value={newEmployee.personnel_number} 
                  onChange={(e) => setNewEmployee({ ...newEmployee, personnel_number: e.target.value })} 
                />
              </div>
              <div className="form-group">
                <label>Фамилия *</label>
                <input 
                  type="text" 
                  placeholder="Введите фамилию" 
                  value={newEmployee.last_name} 
                  onChange={(e) => setNewEmployee({ ...newEmployee, last_name: e.target.value })} 
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Имя *</label>
                <input 
                  type="text" 
                  placeholder="Введите имя" 
                  value={newEmployee.first_name} 
                  onChange={(e) => setNewEmployee({ ...newEmployee, first_name: e.target.value })} 
                />
              </div>
              <div className="form-group">
                <label>Отчество</label>
                <input 
                  type="text" 
                  placeholder="Введите отчество" 
                  value={newEmployee.middle_name} 
                  onChange={(e) => setNewEmployee({ ...newEmployee, middle_name: e.target.value })} 
                />
              </div>
              <div className="form-group">
                <label>Должность</label>
                <select 
                  value={newEmployee.position_id} 
                  onChange={(e) => setNewEmployee({ ...newEmployee, position_id: e.target.value })}
                >
                  <option value="">Выберите должность</option>
                  {positions.map(pos => (
                    <option key={pos.id} value={pos.id}>{pos.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Подразделение</label>
                <select 
                  value={newEmployee.department_id} 
                  onChange={(e) => setNewEmployee({ ...newEmployee, department_id: e.target.value })}
                >
                  <option value="">Выберите подразделение</option>
                  {departments?.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setIsModalOpen(false)}>Отмена</button>
              <button className="btn-primary" onClick={addEmployee} disabled={loading}>
                {loading ? 'Добавление...' : 'Добавить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для редактирования сотрудника */}
      {isEditModalOpen && employeeToEdit && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Редактирование сотрудника</h3>
              <button className="modal-close" onClick={() => !loading && setIsEditModalOpen(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Табельный номер</label>
                <input 
                  type="text" 
                  placeholder="Введите табельный номер" 
                  value={employeeToEdit.personnel_number || ''} 
                  onChange={(e) => setEmployeeToEdit({ ...employeeToEdit, personnel_number: e.target.value })} 
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>Фамилия *</label>
                <input 
                  type="text" 
                  placeholder="Введите фамилию" 
                  value={employeeToEdit.last_name} 
                  onChange={(e) => setEmployeeToEdit({ ...employeeToEdit, last_name: e.target.value })} 
                  autoFocus
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>Имя *</label>
                <input 
                  type="text" 
                  placeholder="Введите имя" 
                  value={employeeToEdit.first_name} 
                  onChange={(e) => setEmployeeToEdit({ ...employeeToEdit, first_name: e.target.value })} 
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>Отчество</label>
                <input 
                  type="text" 
                  placeholder="Введите отчество" 
                  value={employeeToEdit.middle_name || ''} 
                  onChange={(e) => setEmployeeToEdit({ ...employeeToEdit, middle_name: e.target.value })} 
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>Должность</label>
                <select 
                  value={employeeToEdit.position_id || ''} 
                  disabled={true}
                  className="disabled-field"
                >
                  <option value="">Выберите должность</option>
                  {positions.map(pos => (
                    <option key={pos.id} value={pos.id}>{pos.name}</option>
                  ))}
                </select>
                <small className="form-hint" style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                  Для смены должности используйте кнопку ниже
                </small>
              </div>
              <div className="form-group">
                <label>Подразделение</label>
                <select 
                  value={employeeToEdit.department_id || ''} 
                  onChange={(e) => {
                    const departmentId = e.target.value;
                    const department = departments?.find(d => d.id === parseInt(departmentId));
                    setEmployeeToEdit({ 
                      ...employeeToEdit, 
                      department_id: departmentId,
                      department: department ? department.name : '—'
                    });
                  }}
                  disabled={loading}
                >
                  <option value="">Выберите подразделение</option>
                  {departments?.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Блок для смены должности */}
              <div className="form-group position-change-group">
                <label>Смена должности</label>
                <div className="position-change-row">
                  <select 
                    value={selectedNewPositionId} 
                    onChange={(e) => setSelectedNewPositionId(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Выберите новую должность</option>
                    {positions
                      .filter(p => p.id !== (employeeToEdit?.position_id || employeeToEdit?.positionId))
                      .map(pos => (
                        <option key={pos.id} value={pos.id}>{pos.name}</option>
                      ))}
                  </select>
                  <button 
                    type="button"
                    className="btn-change-position"
                    onClick={() => {
                      if (selectedNewPositionId && employeeToEdit) {
                        handleOpenPositionChange(employeeToEdit, selectedNewPositionId);
                      } else {
                        showToast('Выберите новую должность', 'error');
                      }
                    }}
                    disabled={loading || !selectedNewPositionId}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                      <path d="M17 3l4 4-7 7H10v-4l7-7z" />
                      <path d="M3 21h18" />
                    </svg>
                    Изменить должность
                  </button>
                </div>
                <small className="form-hint">
                  При смене должности откроется окно выбора обучений для сохранения
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => !loading && setIsEditModalOpen(false)} disabled={loading}>
                Отмена
              </button>
              <button className="btn-primary" onClick={updateEmployee} disabled={loading}>
                {loading ? 'Сохранение...' : 'Сохранить изменения'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения удаления */}
      {isDeleteModalOpen && employeeToDelete && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header delete-header">
              <h3>Подтверждение удаления</h3>
              <button className="modal-close" onClick={closeDeleteModal}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body delete-body">
              <div className="delete-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <p>Вы уверены, что хотите удалить сотрудника?</p>
              <p className="delete-position-name">"{employeeToDelete.full_name}"</p>
              <div className="delete-warning">
                <strong>Информация о сотруднике:</strong><br />
                Табельный номер: {employeeToDelete.personnel_number}<br />
                Должность: {employeeToDelete.position}<br />
                Подразделение: {employeeToDelete.department}
              </div>
            </div>
            <div className="modal-footer delete-footer">
              <button className="btn-secondary" onClick={closeDeleteModal}>
                Отмена
              </button>
              <button className="btn-danger" onClick={confirmDelete} disabled={loading}>
                {loading ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно смены должности */}
      <ChangePositionModal
        isOpen={isChangePositionModalOpen}
        onClose={() => {
          setIsChangePositionModalOpen(false);
          setSelectedPositionChange({ newPositionId: null, employee: null, currentTrainings: [] });
        }}
        employee={selectedPositionChange.employee}
        currentPositionId={selectedPositionChange.employee?.position_id}
        newPositionId={selectedPositionChange.newPositionId}
        positions={positions}
        currentTrainings={selectedPositionChange.currentTrainings}
        onConfirm={confirmPositionChange}
        isLoading={loading}
      />
    </div>
  );
};

export default EmployeesTab;